/**
 * useUnifiedAutoSave.ts
 * Système unifié de sauvegarde automatique
 * Combine localStorage (backup local) + Supabase (si projet ouvert)
 * Remplace useAutoSave.ts et useAutoSaveProject.ts
 */
import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../services/StateService';
import { logger } from '../utils/ProfessionalLogger';
import { useAuthContext } from '../components/auth/AuthProvider';
import { UnifiedSaveService } from '../services/UnifiedSaveService';
import { useToast } from '../components/react/common/ToastProvider';
import { useOnlineStatus } from './useOnlineStatus';

// Configuration
const STORAGE_KEY = 'snapbooth_autosave';
const BACKUP_KEY = 'snapbooth_backup';
const DEBOUNCE_MS = 2000; // Délai avant sauvegarde après modification
const PERIODIC_SAVE_INTERVAL = 30000; // Sauvegarde périodique toutes les 30 secondes
const MAX_RETRIES = 3;
const _MIN_SAVE_INTERVAL = 5000; // Minimum 5 secondes entre deux sauvegardes Supabase

import type { ShapeData } from '../types/ShapeTypes';

interface AutoSaveData {
  shapes: ShapeData[];
  canvas: {
    templateWidth: number;
    templateHeight: number;
    backgroundColor: string;
    documentName: string;
  };
  timestamp: number;
  version: string;
  projectId?: string | null; // ID du projet Supabase si disponible
}

interface UseUnifiedAutoSaveOptions {
  projectId?: string | null; // ID du projet Supabase (optionnel)
  enabled?: boolean; // Activer/désactiver la sauvegarde
  saveToCloud?: boolean; // Sauvegarder dans Supabase (nécessite projectId et auth)
  onSaveSuccess?: () => void;
  onSaveError?: (error: Error, source: 'local' | 'cloud') => void;
}

/**
 * Nettoie le localStorage si le quota est dépassé
 */
function clearOldStorageData(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('snapbooth_') || key.startsWith('konva_'))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.sort();
    const keysToKeep = [STORAGE_KEY, BACKUP_KEY];
    keysToRemove.forEach(key => {
      if (!keysToKeep.includes(key)) {
        try {
          localStorage.removeItem(key);
        } catch (_e) {
          // Ignorer les erreurs de suppression
        }
      }
    });
  } catch (error) {
    logger.warn('Erreur lors du nettoyage du localStorage:', error);
  }
}

/**
 * Réduit la taille des données en limitant le nombre de shapes
 */
function compressData(data: AutoSaveData): AutoSaveData {
  const maxShapes = 100;
  if (data.shapes.length > maxShapes) {
    logger.warn(
      `Réduction de ${data.shapes.length} à ${maxShapes} shapes pour économiser l'espace`
    );
    return {
      ...data,
      shapes: data.shapes.slice(0, maxShapes),
    };
  }
  return data;
}

/**
 * Sauvegarde dans localStorage avec retry et backoff exponentiel
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- helper récursif (appelé en interne pour le retry), conservé pour usage futur
async function saveToLocalStorage(
  data: AutoSaveData,
  retries: number = MAX_RETRIES,
  delay: number = 1000,
  isRetry: boolean = false
): Promise<boolean> {
  try {
    const compressedData = compressData(data);
    const serialized = JSON.stringify(compressedData);

    // Vérifier la taille approximative
    const sizeInMB = serialized.length / (1024 * 1024);
    if (sizeInMB > 4) {
      logger.warn(`Données volumineuses: ${sizeInMB.toFixed(2)}MB. Nettoyage du localStorage...`);
      clearOldStorageData();
    }

    localStorage.setItem(STORAGE_KEY, serialized);

    // Créer aussi un backup
    try {
      localStorage.setItem(BACKUP_KEY, serialized);
    } catch (backupError) {
      logger.warn('Impossible de créer le backup:', backupError);
    }

    return true;
  } catch (error: unknown) {
    // Gérer spécifiquement l'erreur de quota
    const err = error as { name?: string; message?: string };
    if (err?.name === 'QuotaExceededError' || err?.message?.includes('quota')) {
      logger.warn('Quota localStorage dépassé, nettoyage en cours...');

      if (!isRetry) {
        clearOldStorageData();
        await new Promise(resolve => setTimeout(resolve, 500));
        return saveToLocalStorage(compressData(data), 0, delay, true);
      }

      // Si le nettoyage n'a pas aidé, supprimer l'ancienne sauvegarde
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(BACKUP_KEY);
        const compressedData = compressData(data);
        const serialized = JSON.stringify(compressedData);
        localStorage.setItem(STORAGE_KEY, serialized);
        return true;
      } catch (finalError) {
        logger.error('Impossible de sauvegarder même après nettoyage:', finalError);
        return false;
      }
    }

    // Autres erreurs : retry normal
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return saveToLocalStorage(data, retries - 1, delay * 2, true);
    }

    logger.error('Échec de la sauvegarde localStorage après tous les retries:', error);
    return false;
  }
}

// NOTE: La fonction saveToCloud a été supprimée car elle n'est plus utilisée.
// Le hook utilise maintenant UnifiedSaveService.save() qui gère à la fois localStorage et Supabase.

/**
 * Charge les données sauvegardées depuis localStorage
 */
export function loadAutoSave(): AutoSaveData | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved) as AutoSaveData;
      if (data.shapes && Array.isArray(data.shapes) && data.canvas) {
        return data;
      }
    }

    // Essayer le backup si la sauvegarde principale échoue
    const backup = localStorage.getItem(BACKUP_KEY);
    if (backup) {
      const data = JSON.parse(backup) as AutoSaveData;
      if (data.shapes && Array.isArray(data.shapes) && data.canvas) {
        logger.warn('Chargé depuis le backup');
        return data;
      }
    }
  } catch (error) {
    logger.error("Échec du chargement de l'auto-save:", error);
  }
  return null;
}

/**
 * Efface les données sauvegardées
 */
export function clearAutoSave(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(BACKUP_KEY);
}

/**
 * Hook unifié de sauvegarde automatique
 * Combine localStorage + Supabase
 */
export function useUnifiedAutoSave(options: UseUnifiedAutoSaveOptions = {}): void {
  const {
    projectId = null,
    enabled = true,
    saveToCloud: saveToCloudOption = true,
    onSaveSuccess,
    onSaveError,
  } = options;

  // État depuis le store
  const shapes = useAppStore(state => state.shapes);
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const backgroundColor = useAppStore(state => state.canvas.backgroundColor);
  const documentName = useAppStore(state => state.canvas.documentName);
  const markAsSaved = useAppStore(state => state.markAsSaved);
  const markAsUnsaved = useAppStore(state => state.markAsUnsaved);
  const setSaveStatus = useAppStore(state => state.setSaveStatus);
  const { addToast } = useToast();

  // Auth context (optionnel - peut échouer si pas dans AuthProvider)
  let user = null;
  try {
    const authContext = useAuthContext();
    user = authContext?.user || null;
  } catch (_e) {
    // AuthProvider peut ne pas être disponible, continuer sans
    logger.debug('AuthProvider non disponible, sauvegarde locale uniquement');
  }

  // A16 : détection réseau réactive.
  const isOnline = useOnlineStatus();

  // Refs pour gérer les timers et éviter les sauvegardes trop fréquentes
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>('');
  const lastCloudSaveRef = useRef<number>(0);
  const _isSavingRef = useRef<boolean>(false);
  // A03 : pause de la synchro cloud tant qu'un conflit n'est pas résolu (évite de
  // re-prompter en boucle). forceOnceRef = écrasement explicite (« Écraser »).
  const conflictPausedRef = useRef<boolean>(false);
  const forceOnceRef = useRef<boolean>(false);

  /**
   * Fonction de sauvegarde principale
   * CORRIGÉ: Utilise SaveService centralisé
   */
  const save = useCallback(async () => {
    if (!enabled) {
      return;
    }

    // Vérifier si les données ont changé
    const currentState = useAppStore.getState();
    const data = {
      shapes: currentState.shapes,
      canvas: {
        templateWidth: currentState.canvas.templateWidth,
        templateHeight: currentState.canvas.templateHeight,
        backgroundColor: currentState.canvas.backgroundColor,
        documentName: currentState.canvas.documentName,
      },
    };
    const serialized = JSON.stringify(data);
    // A03 : un écrasement explicite (« Écraser quand même ») doit pouvoir
    // re-déclencher la sauvegarde même si rien n'a changé depuis le blocage.
    const useForce = forceOnceRef.current;
    forceOnceRef.current = false; // one-shot
    if (serialized === lastSavedRef.current && !useForce) {
      return; // Pas de changement, pas de sauvegarde
    }

    // A16 : si hors ligne → local uniquement (état 'offline').
    // A03 : si un conflit est en attente de décision → cloud en pause (sauf force).
    const shouldSaveCloud =
      saveToCloudOption &&
      !!projectId &&
      !!user &&
      isOnline &&
      (!conflictPausedRef.current || useForce);

    setSaveStatus('saving');

    try {
      const unifiedSaveService = UnifiedSaveService.getInstance();
      const result = await unifiedSaveService.save({
        projectId: projectId || null,
        saveToCloud: shouldSaveCloud,
        saveToLocal: true,
        priority: 'normal',
        force: useForce,
        onSuccess: () => {
          markAsSaved();
          onSaveSuccess?.();
        },
        onError: error => {
          // Note: result peut ne pas être défini ici si l'erreur survient avant l'assignation
          const source = result && result.cloud === false ? 'cloud' : 'local';
          setSaveStatus('error', error.message);
          onSaveError?.(error, source);
        },
      });

      if (result && result.success) {
        // A03 : conflit BLOQUÉ → on n'a PAS écrasé. On ouvre la modal de décision
        // (Recharger / Écraser / Annuler) et on met la synchro cloud en pause.
        if (result.blocked) {
          conflictPausedRef.current = true;
          setSaveStatus('conflict');
          window.dispatchEvent(new CustomEvent('snapbooth:save-conflict'));
          // NB : on NE met PAS à jour lastSavedRef (pour pouvoir re-tenter).
          return;
        }

        lastSavedRef.current = serialized;
        if (result.cloud) {
          lastCloudSaveRef.current = Date.now();
          conflictPausedRef.current = false; // conflit résolu
        }
        // A16 : sauvegarde locale OK mais hors ligne → 'offline'.
        if (!isOnline && shouldSaveCloud === false) {
          setSaveStatus('offline');
        } else if (conflictPausedRef.current) {
          // A03 : sauvegarde locale pendant un conflit non résolu → on garde
          // l'état 'conflict' (markAsSaved avait mis 'saved', on le corrige).
          setSaveStatus('conflict');
        }
        // A03 : conflit écrasé sur décision explicite (force) — info discrète.
        if (result.conflicted && useForce) {
          addToast({
            type: 'success',
            message: 'Conflit résolu — votre version a été enregistrée.',
            duration: 5000,
          });
        }
      }
    } catch (error) {
      // Erreur déjà gérée par SaveService
      const err = error instanceof Error ? error : new Error(String(error));
      setSaveStatus('error', err.message);
      onSaveError?.(err, 'local');
    }
  }, [
    enabled,
    projectId,
    saveToCloudOption,
    user,
    isOnline,
    markAsSaved,
    setSaveStatus,
    addToast,
    onSaveSuccess,
    onSaveError,
  ]);

  // A03 : écoute la décision de l'utilisateur depuis la modal de conflit.
  // « Écraser quand même » → on force la sauvegarde cloud (bypass conflit).
  // « Annuler » → on reste en pause (le travail local est protégé).
  // « Recharger » est géré par la modal (rechargement de la page).
  useEffect(() => {
    if (!enabled) return;
    const handleOverwrite = () => {
      forceOnceRef.current = true;
      conflictPausedRef.current = false;
      save();
    };
    const handleCancel = () => {
      // On garde conflictPausedRef = true → la synchro cloud reste suspendue
      // jusqu'à un nouvel écrasement explicite ou un rechargement de page.
      conflictPausedRef.current = true;
    };
    window.addEventListener('snapbooth:conflict-overwrite', handleOverwrite);
    window.addEventListener('snapbooth:conflict-cancel', handleCancel);
    return () => {
      window.removeEventListener('snapbooth:conflict-overwrite', handleOverwrite);
      window.removeEventListener('snapbooth:conflict-cancel', handleCancel);
    };
  }, [enabled, save]);

  // Effect pour déclencher la sauvegarde après un délai (debounce)
  useEffect(() => {
    if (!enabled) return;

    markAsUnsaved();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      save();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [
    enabled,
    shapes,
    templateWidth,
    templateHeight,
    backgroundColor,
    documentName,
    save,
    markAsUnsaved,
  ]);

  // Sauvegarde périodique basée sur l'état Zustand (remplace setInterval)
  useEffect(() => {
    if (!enabled) return;

    let timeoutRef: NodeJS.Timeout | null = null;

    const schedulePeriodicSave = () => {
      // Nettoyer le timeout précédent
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }

      // Programmer la prochaine sauvegarde
      timeoutRef = setTimeout(() => {
        const currentStatus = useAppStore.getState().save.status;
        // A16 : inclure 'offline' pour retenter au prochain cycle périodique
        // si la connexion est revenue entre temps.
        if (
          currentStatus === 'unsaved' ||
          currentStatus === 'idle' ||
          currentStatus === 'offline'
        ) {
          save();
        }
        // Réprogrammer pour la prochaine fois
        schedulePeriodicSave();
      }, PERIODIC_SAVE_INTERVAL);
    };

    // S'abonner aux changements d'état pour déclencher la sauvegarde périodique
    // P3 : zustand v4 `subscribe` n'accepte plus selector + callback en signature.
    // On utilise la signature simple : observer le store entier et comparer le statut.
    let lastStatus: string | undefined;
    const unsubscribe = useAppStore.subscribe(state => {
      const status = state.save.status;
      if (status !== lastStatus) {
        lastStatus = status;
        // A16 : inclure 'offline' pour que le cycle périodique se réenclenche.
        if (status === 'unsaved' || status === 'idle' || status === 'offline') {
          schedulePeriodicSave();
        }
      }
    });

    // Démarrer le cycle de sauvegarde périodique
    schedulePeriodicSave();

    return () => {
      unsubscribe();
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, [enabled, save]);

  // A16 : retry automatique au retour de la connexion.
  // Si le statut est 'offline' quand isOnline repasse à true, on relance
  // immédiatement une sauvegarde cloud pour synchroniser ce qui avait été
  // conservé en local seulement.
  useEffect(() => {
    if (!enabled || !isOnline) return;
    const currentStatus = useAppStore.getState().save.status;
    if (currentStatus === 'offline') {
      logger.info('🔄 Connexion rétablie — retry de la sauvegarde cloud');
      addToast({ type: 'info', message: 'Connexion rétablie — synchronisation en cours…' });
      save();
    }
  }, [isOnline, enabled, save, addToast]);

  // Sauvegarder avant de quitter la page
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const saveStatus = useAppStore.getState().save.status;
      // A16 : 'offline' = sauvegardé localement mais pas dans le cloud → alerter.
      if (saveStatus === 'unsaved' || saveStatus === 'offline') {
        e.preventDefault();
        e.returnValue =
          saveStatus === 'offline'
            ? 'Modifications sauvegardées localement mais non synchronisées (hors ligne).'
            : 'Vous avez des modifications non sauvegardées.';
        save();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, save]);
}

/**
 * Hook pour restaurer les données au chargement
 */
export function useAutoSaveRestore(): void {
  useEffect(() => {
    try {
      const store = useAppStore.getState();
      if (!store || !store.loadShapes || !store.setTemplateDimensions) {
        // Store pas encore prêt, réessayer après un court délai
        const timeoutId = setTimeout(() => {
          const retryStore = useAppStore.getState();
          if (retryStore && retryStore.loadShapes && retryStore.setTemplateDimensions) {
            const savedData = loadAutoSave();
            if (savedData) {
              retryStore.loadShapes(savedData.shapes);
              retryStore.setTemplateDimensions(
                savedData.canvas.templateWidth,
                savedData.canvas.templateHeight
              );
              retryStore.setBackgroundColor(savedData.canvas.backgroundColor);
              retryStore.setDocumentName(savedData.canvas.documentName);
              logger.info("Restauré depuis l'auto-save", {
                timestamp: new Date(savedData.timestamp).toLocaleString(),
              });
            }
          }
        }, 100);
        return () => clearTimeout(timeoutId);
      }

      const savedData = loadAutoSave();
      if (savedData) {
        store.loadShapes(savedData.shapes);
        store.setTemplateDimensions(
          savedData.canvas.templateWidth,
          savedData.canvas.templateHeight
        );
        store.setBackgroundColor(savedData.canvas.backgroundColor);
        store.setDocumentName(savedData.canvas.documentName);
        logger.info("Restauré depuis l'auto-save", {
          timestamp: new Date(savedData.timestamp).toLocaleString(),
        });
      }
    } catch (error) {
      logger.warn("Erreur lors de la restauration de l'auto-save:", error);
    }
  }, []); // Exécuter une seule fois au montage
}

export default useUnifiedAutoSave;
